import api from "./api";
import { ResponseInterface } from "../interfaces";
import { ExchangeRateHistoricalDto } from "../interfaces/Dtos";
import {
  CacheTTL,
  cacheKey,
  cached,
  isSuccessfulResponse,
} from "./requestCache";

const baseURLAPI = process.env.REACT_APP_API_HOST;

/** Local calendar day, matching how the rate is keyed by ValueDate. */
const dayOf = (date: string): string => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
  const day = `${parsed.getDate()}`.padStart(2, "0");
  return `${parsed.getFullYear()}-${month}-${day}`;
};

/**
 * The key is the exact `date` the caller passed, not its calendar day.
 *
 * Keying by day looks tempting — a payment table asks once per row and the rows
 * usually share a date — but it is wrong here: the business confirmed the rate
 * moves up to three times a day (09:00, 13:00, 17:00), and `date` travels
 * verbatim as ValueDate in the request body. A day-wide key would hand a
 * payment made at 15:00 the rate fetched for one made at 10:00, and the value
 * goes straight to screen as an amount in Bs. A cache key must never be coarser
 * than the request it stands for.
 *
 * The day is still used for one thing: choosing the TTL. A past date's rate is
 * settled and worth keeping for the session; today's expires, because it can
 * still move.
 */
export const getDollarExchangeRate = async (
  date: string,
  countryID: number = 236
): Promise<ResponseInterface<number>> => {
  const isToday = dayOf(date) === dayOf(new Date().toISOString());

  return cached(
    cacheKey("getDollarExchangeRate", countryID, date),
    () => fetchDollarExchangeRate(date, countryID),
    isToday ? CacheTTL.RATE_TODAY : CacheTTL.RATE_PAST,
    isSuccessfulResponse
  );
};

const fetchDollarExchangeRate = async (
  date: string,
  countryID: number
): Promise<ResponseInterface<number>> => {
  const body = JSON.stringify({
    ValueDate: date,
    CountryID: countryID,
    CurrencyLocalID: 1,
    ExchangeCurrencyID: 2,
  });

  const uri = `${baseURLAPI}/Currency/ExchangeRate`;

  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      return response.data;
    }
    return {
      model: null,
      didError: true,
      message: "",
      errorMessage: "Hubo un error al obtener los cupones disponibles",
    };
  } catch (error) {
    return (error as any)?.response?.data;
  }
};

export const getHistoricalExchangeRates = async ():
 Promise<ResponseInterface<ExchangeRateHistoricalDto[]>> => {
  
  const uri = `${baseURLAPI}/GetHistoricalExchangeRate`;

  try {
    const response = await api.get<ResponseInterface<ExchangeRateHistoricalDto[]>>(uri, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 200) {
      return response.data;
    }
    return {
      model: null,
      didError: true,
      message: "",
      errorMessage: "Hubo un error al obtener el histórico de tasas de cambio",
    };
  } catch (error) {
    return (error as any)?.response?.data;
  }
};